import { useQuery, useMutation } from "@tanstack/react-query";
import { trackEvent } from "@/hooks/useTracking";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  Calendar,
  List,
  Loader2,
  Sparkles,
  Search,
  Flame,
  TrendingUp,
  Globe,
  SlidersHorizontal,
  LayoutGrid,
  CalendarCheck,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import type { Location, Post, Keyword } from "@shared/schema";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

import { DD, POST_TYPES } from "@/components/posts/constants";
import { computeStreak, getPostDate } from "@/components/posts/utils";
import { getPostTags } from "@/components/posts/types";
import { StatsBar } from "@/components/posts/StatsBar";
import { EmptyState } from "@/components/posts/EmptyState";
import { GenerateModal } from "@/components/posts/GenerateModal";
import { PostCard } from "@/components/posts/PostCard";
import { PostEditor } from "@/components/posts/PostEditor";
import { CalendarView } from "@/components/posts/CalendarView";
import { KanbanBoard } from "@/components/posts/KanbanBoard";
import { RtdOptOut, RtdOptOutBanner } from "@/components/rtd-opt-out";

function PostPublishedReward({ keyword, onDone }: { keyword?: string; onDone: () => void }) {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 600),
      setTimeout(() => setPhase(2), 1400),
      setTimeout(() => setPhase(3), 2200),
      setTimeout(() => onDone(), 3800),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onDone]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="post-reward-overlay">
      <div className="text-center space-y-4 animate-in fade-in zoom-in-95 duration-300">
        <div className="text-5xl mb-2">🎉</div>
        <h2 className="text-2xl font-bold text-white">Post Published Successfully</h2>
        <div className="space-y-2 mt-4">
          <div className={`flex items-center justify-center gap-2 transition-all duration-500 ${phase >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <TrendingUp className="w-4 h-4 text-orange-400" />
            <span className="text-orange-400 font-semibold">+2 visibility signals sent</span>
          </div>
          <div className={`flex items-center justify-center gap-2 transition-all duration-500 ${phase >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <Globe className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400 font-semibold">Indexed into Google ecosystem</span>
          </div>
          <div className={`flex items-center justify-center gap-2 transition-all duration-500 ${phase >= 3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <Search className="w-4 h-4 text-orange-400" />
            <span className="text-orange-400 font-semibold">Boosting {keyword || "local"} positioning</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function StreakBadge({ streak }: { streak: number }) {
  if (streak === 0) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-orange-600/20 to-red-600/20 border border-orange-500/30" data-testid="streak-badge">
      <Flame className="w-4 h-4 text-orange-400" />
      <span className="text-sm font-bold text-orange-300">{streak}-day streak</span>
      <span className="text-[10px] text-orange-400/70 uppercase tracking-wider hidden sm:inline">Don't break it!</span>
    </div>
  );
}

export default function PostsPage() {
  const { selectedLocationId } = useLocationStore();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editPost, setEditPost] = useState<Post | null>(null);
  const [activeTab, setActiveTab] = useState("queue");
  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());
  const [showReward, setShowReward] = useState(false);
  const [rewardKeyword, setRewardKeyword] = useState<string | undefined>();
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [focusedPostId, setFocusedPostId] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: locations } = useQuery<Location[]>({ queryKey: ["/api/locations"] });
  const locationId = selectedLocationId || locations?.[0]?.id;
  const location = locations?.find(l => l.id === locationId);

  const { data: posts = [], isLoading } = useQuery<Post[]>({
    queryKey: ["/api/posts", locationId],
    enabled: !!locationId,
  });

  const { data: keywords = [] } = useQuery<Keyword[]>({
    queryKey: ["/api/keywords", locationId],
    enabled: !!locationId,
  });

  const streak = useMemo(() => computeStreak(posts), [posts]);

  const filteredPosts = useMemo(() => {
    let result = posts;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        (p.content || "").toLowerCase().includes(q) ||
        (p.title || "").toLowerCase().includes(q) ||
        getPostTags(p).some((t: string) => t.toLowerCase().includes(q))
      );
    }
    if (filterStatus !== "all") {
      result = result.filter(p => p.status === filterStatus);
    }
    if (filterType !== "all") {
      result = result.filter(p => p.postType === filterType);
    }
    return result;
  }, [posts, searchQuery, filterStatus, filterType]);

  const sortedPosts = useMemo(() => {
    return [...filteredPosts].sort((a, b) => {
      const da = getPostDate(a)?.getTime() || 0;
      const db = getPostDate(b)?.getTime() || 0;
      return db - da;
    });
  }, [filteredPosts]);

  const schedulablePosts = useMemo(
    () => posts.filter(p => p.status !== "scheduled" && p.status !== "published"),
    [posts]
  );

  const reviewedIdsRef = useRef<Set<string>>(new Set());

  const [imageProgress, setImageProgress] = useState<{ completed: number; failed: number; total: number; done?: boolean } | null>(null);
  const imageAbortRef = useRef<AbortController | null>(null);

  const startBatchImageGeneration = useCallback(async (postIds: string[]) => {
    if (!locationId || postIds.length === 0) return;
    setImageProgress({ completed: 0, failed: 0, total: postIds.length });

    const abortController = new AbortController();
    imageAbortRef.current = abortController;

    try {
      const res = await fetch("/api/posts/generate-images-batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ locationId, postIds }),
        signal: abortController.signal,
      });

      const reader = res.body?.getReader();
      if (!reader) return;

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              setImageProgress(data);
              if (data.done) {
                queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
              }
            } catch {}
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error("Batch image generation error:", err);
      }
    } finally {
      imageAbortRef.current = null;
      setTimeout(() => {
        setImageProgress(null);
        queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      }, 3000);
    }
  }, [locationId]);

  const generateMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", `/api/posts/generate/${locationId}`);
    },
    onSuccess: async (res) => {
      const data = await res.json();
      trackEvent('gbp_post_created', { method: 'ai_generate', count: data.count });
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "rtd-progress"] });
      toast({ title: `${data.count || "New"} posts generated`, description: "Posts are ready for your review. Now generating images..." });
      setActiveTab("queue");
      setGenerateModalOpen(false);

      const postIds = (data.posts || []).map((p: any) => p.id).filter(Boolean);
      if (postIds.length > 0) {
        startBatchImageGeneration(postIds);
      }
    },
    onError: (err: Error) => {
      toast({ title: "Generation failed", description: err.message, variant: "destructive" });
      setGenerateModalOpen(false);
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (post: Post) => {
      let gbpPublished = false;
      try {
        const res = await apiRequest("POST", "/api/gbp/posts/publish", {
          locationId,
          postText: post.content,
          ctaType: post.ctaType,
          ctaUrl: post.ctaUrl,
          mediaUrl: post.mediaUrl,
          postType: post.postType,
          couponCode: post.couponCode,
          eventStartDate: post.eventStartDate,
          eventEndDate: post.eventEndDate,
          title: post.title,
        });
        const data = await res.json();
        gbpPublished = data?.published === true;
      } catch {
        gbpPublished = false;
      }
      await apiRequest("PATCH", `/api/posts/${post.id}`, {
        status: gbpPublished ? "published" : "scheduled",
        publishedAt: gbpPublished ? new Date().toISOString() : undefined,
        approvalStatus: "approved",
      });
      return { post, gbpPublished };
    },
    onSuccess: ({ post, gbpPublished }) => {
      trackEvent('gbp_post_published', { post_id: post.id });
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "rtd-progress"] });
      if (gbpPublished) {
        handlePublishSuccess(post.targetKeyword || undefined);
      } else {
        toast({ title: "Post saved & scheduled", description: "Could not publish to Google — your GBP connection needs re-authorization." });
      }
    },
    onError: (err: Error) => toast({ title: "Publish failed", description: err.message, variant: "destructive" }),
  });

  const scheduleMutation = useMutation({
    mutationFn: (post: Post) => apiRequest("PATCH", `/api/posts/${post.id}`, { status: "scheduled", scheduledAt: new Date().toISOString(), approvalStatus: "approved" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "rtd-progress"] });
      toast({ title: "Post scheduled" });
    },
    onError: (err: Error) => toast({ title: "Failed to schedule", description: err.message, variant: "destructive" }),
  });

  const scheduleAllMutation = useMutation({
    mutationFn: async (toSchedule: Post[]) => {
      const results = await Promise.allSettled(
        toSchedule.map(p =>
          apiRequest("PATCH", `/api/posts/${p.id}`, {
            status: "scheduled",
            scheduledAt: p.scheduledAt || new Date().toISOString(),
            approvalStatus: "approved",
          })
        )
      );
      const scheduled = results.filter(r => r.status === "fulfilled").length;
      return { scheduled, failed: results.length - scheduled };
    },
    onSuccess: ({ scheduled, failed }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "rtd-progress"] });
      if (failed > 0) {
        toast({ title: `${scheduled} scheduled, ${failed} failed`, description: "Some posts couldn't be scheduled. Try again.", variant: "destructive" });
      } else {
        toast({ title: `${scheduled} post${scheduled !== 1 ? "s" : ""} scheduled`, description: "Your queue is set." });
      }
    },
    onError: (err: Error) => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      toast({ title: "Failed to schedule all", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (post: Post) => apiRequest("DELETE", `/api/posts/${post.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/posts", locationId] });
      toast({ title: "Post deleted" });
    },
    onError: (err: Error) => toast({ title: "Failed to delete", description: err.message, variant: "destructive" }),
  });

  function prevMonth() {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  }

  function openEditor(post: Post | null = null) {
    trackEvent('gbp_poster_opened', { editing: !!post });
    reviewedIdsRef.current.clear();
    setEditPost(post);
    setEditorOpen(true);
  }

  const handleScheduled = useCallback((postId: string): boolean => {
    reviewedIdsRef.current.add(postId);
    const next = [...posts]
      .filter(p =>
        p.id !== postId &&
        !reviewedIdsRef.current.has(p.id) &&
        p.status !== "scheduled" &&
        p.status !== "published"
      )
      .sort((a, b) => (getPostDate(b)?.getTime() || 0) - (getPostDate(a)?.getTime() || 0))[0];
    if (next) {
      setEditPost(next);
      return true;
    }
    reviewedIdsRef.current.clear();
    return false;
  }, [posts]);

  function openDay(clickedDate: string) {
    setEditPost(null);
    setSelectedDay(clickedDate);
    setEditorOpen(true);
  }

  const handlePublishSuccess = useCallback((kw?: string) => {
    setRewardKeyword(kw);
    setShowReward(true);
  }, []);

  const handleRewardDone = useCallback(() => {
    setShowReward(false);
    toast({ title: "Post published to Google!" });
  }, [toast]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "p" || e.key === "P") {
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
        if (editorOpen || generateModalOpen) return;

        if (focusedPostId) {
          const post = posts.find(p => p.id === focusedPostId);
          if (post && post.status !== "published") {
            e.preventDefault();
            publishMutation.mutate(post);
          }
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [focusedPostId, posts, editorOpen, generateModalOpen, publishMutation]);

  useEffect(() => {
    function handleFocus(e: FocusEvent) {
      const target = e.target as HTMLElement;
      const postId = target?.closest("[data-post-id]")?.getAttribute("data-post-id");
      if (postId) setFocusedPostId(postId);
    }
    document.addEventListener("focusin", handleFocus);
    return () => document.removeEventListener("focusin", handleFocus);
  }, []);

  if (isLoading) {
    return (
      <div className="p-6 space-y-4" style={{ background: DD.bg }}>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  const hasNoPosts = posts.length === 0;

  return (
    <div className="p-4 sm:p-6 pb-24 space-y-5 overflow-y-auto h-full" style={{ background: DD.bg, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {showReward && <PostPublishedReward keyword={rewardKeyword} onDone={handleRewardDone} />}

      {generateModalOpen && (
        <GenerateModal
          isGenerating={generateMutation.isPending}
          onClose={() => setGenerateModalOpen(false)}
        />
      )}

      {editorOpen && locationId && (
        <PostEditor
          key={editPost?.id ?? "new"}
          locationId={locationId}
          location={location}
          keywords={keywords}
          onClose={() => { setEditorOpen(false); setEditPost(null); setSelectedDay(null); reviewedIdsRef.current.clear(); }}
          editPost={editPost}
          onPublishSuccess={handlePublishSuccess}
          onScheduled={handleScheduled}
          initialDate={selectedDay}
        />
      )}

      {imageProgress && (
        <div
          className="rounded-xl p-4 flex items-center gap-4"
          style={{ background: DD.surface, border: `1px solid ${DD.accentBorder}` }}
          data-testid="image-progress-banner"
        >
          {imageProgress.done ? (
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: DD.accentBg }}>
              <Sparkles className="w-5 h-5" style={{ color: DD.accent }} />
            </div>
          ) : (
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: DD.accent }} />
          )}
          <div className="flex-1">
            <div className="text-sm font-semibold text-white">
              {imageProgress.done
                ? `Image generation complete — ${imageProgress.completed} images created`
                : `Generating images... ${imageProgress.completed} of ${imageProgress.total}`
              }
            </div>
            {!imageProgress.done && (
              <div className="w-full h-1.5 rounded-full mt-2 overflow-hidden" style={{ background: DD.bg }}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ background: DD.accent, width: `${Math.round((imageProgress.completed / imageProgress.total) * 100)}%` }}
                />
              </div>
            )}
            {imageProgress.failed > 0 && (
              <p className="text-xs mt-1" style={{ color: DD.textTertiary }}>{imageProgress.failed} images could not be generated</p>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ color: DD.text }} data-testid="text-posts-title">
            Posts Engine
          </h1>
          <p className="text-sm mt-1" style={{ color: DD.textSecondary }}>
            AI-powered content strategy, scheduling, and performance tracking
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StreakBadge streak={streak} />
          <RtdOptOut
            locationId={locationId}
            taskIds={["posts", "publish_post", "schedule_post"]}
            stageName="Posts"
            warningTitle="Skip Posts Engine for now?"
            warningBody={
              <p>
                Heads up — GBP posts are one of the easiest ways to keep your listing fresh and signal
                activity to Google. Businesses that post weekly typically see more profile views, calls,
                and direction requests. Skipping this step usually means lower engagement and slower
                ranking growth.
              </p>
            }
          />
          {schedulablePosts.length > 0 && (
            <Button
              variant="outline"
              className="text-xs"
              style={{ border: `1px solid ${DD.accentBorder}`, color: DD.accent, background: "transparent" }}
              onClick={() => scheduleAllMutation.mutate(schedulablePosts)}
              disabled={!locationId || scheduleAllMutation.isPending}
              data-testid="button-schedule-all"
            >
              {scheduleAllMutation.isPending
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <CalendarCheck className="w-4 h-4 mr-2" />}
              {scheduleAllMutation.isPending ? "Scheduling..." : `Schedule All (${schedulablePosts.length})`}
            </Button>
          )}
          <Button
            variant="outline"
            className="text-xs"
            style={{ border: `1px solid ${DD.border}`, color: DD.textSecondary, background: "transparent" }}
            onClick={() => { setGenerateModalOpen(true); generateMutation.mutate(); }}
            disabled={!locationId || generateMutation.isPending}
            data-testid="button-generate-posts"
          >
            <Sparkles className="w-4 h-4 mr-2" />{generateMutation.isPending ? "Generating..." : "Generate Posts"}
          </Button>
          <Button className="text-white font-semibold" style={{ background: DD.accent }} onClick={() => openEditor()} data-testid="button-new-post">
            <Plus className="w-4 h-4 mr-1" />New Post
          </Button>
        </div>
      </div>
      <RtdOptOutBanner
        locationId={locationId}
        taskIds={["posts", "publish_post", "schedule_post"]}
        stageName="Posts"
      />

      {hasNoPosts && locationId ? (
        <EmptyState
          locationId={locationId}
          onGenerateMonth={() => { setGenerateModalOpen(true); generateMutation.mutate(); }}
          isGenerating={generateMutation.isPending}
        />
      ) : (
        <>
          <StatsBar posts={posts} keywords={keywords} />

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <TabsList className="bg-transparent border border-white/[0.06] p-0.5 rounded-lg">
                <TabsTrigger value="queue" className="text-xs gap-1.5 data-[state=active]:bg-white/5 data-[state=active]:text-white rounded-md" data-testid="tab-queue">
                  <List className="w-3.5 h-3.5" /> Queue
                </TabsTrigger>
                <TabsTrigger value="calendar" className="text-xs gap-1.5 data-[state=active]:bg-white/5 data-[state=active]:text-white rounded-md" data-testid="tab-calendar">
                  <Calendar className="w-3.5 h-3.5" /> Calendar
                </TabsTrigger>
                <TabsTrigger value="kanban" className="text-xs gap-1.5 data-[state=active]:bg-white/5 data-[state=active]:text-white rounded-md" data-testid="tab-kanban">
                  <LayoutGrid className="w-3.5 h-3.5" /> Drafts & Review
                </TabsTrigger>
              </TabsList>

              {(activeTab === "queue" || activeTab === "kanban") && (
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: DD.textTertiary }} />
                    <Input
                      placeholder="Search posts..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="pl-9 text-xs h-8 w-full sm:w-[200px]"
                      style={{ background: DD.surface, border: `1px solid ${DD.border}`, color: DD.text }}
                      data-testid="input-search-posts"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-8"
                    style={{
                      border: `1px solid ${showFilters ? DD.accentBorder : DD.border}`,
                      color: showFilters ? DD.accent : DD.textSecondary,
                      background: "transparent",
                    }}
                    onClick={() => setShowFilters(!showFilters)}
                    data-testid="button-toggle-filters"
                  >
                    <SlidersHorizontal className="w-3 h-3 mr-1" />
                    Filters
                  </Button>
                </div>
              )}
            </div>

            {showFilters && (activeTab === "queue" || activeTab === "kanban") && (
              <div className="flex items-center gap-3 p-2.5 rounded-xl mt-2" style={{ background: DD.surface, border: `1px solid ${DD.border}` }}>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-white/30 uppercase font-semibold">Status</span>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="h-7 w-[110px] bg-[#0a0a0a] border-[rgba(255,255,255,0.06)] text-xs" data-testid="select-filter-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="review">Review</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-white/30 uppercase font-semibold">Type</span>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="h-7 w-[110px] bg-[#0a0a0a] border-[rgba(255,255,255,0.06)] text-xs" data-testid="select-filter-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {POST_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <TabsContent value="queue" className="mt-4">
              {sortedPosts.length === 0 ? (
                <div className="text-center py-16" data-testid="queue-empty">
                  <div className="text-4xl mb-3">📝</div>
                  <p className="text-white/50">No posts match your filters</p>
                  <p className="text-white/20 text-sm mt-1">Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="post-queue">
                  {sortedPosts.map(post => (
                    <PostCard
                      key={post.id}
                      post={post}
                      keywords={keywords}
                      onEdit={openEditor}
                      onPreview={openEditor}
                      onPublish={(p) => publishMutation.mutate(p)}
                      onSchedule={(p) => scheduleMutation.mutate(p)}
                      onDelete={(p) => deleteMutation.mutate(p)}
                      onGenerateImage={openEditor}
                      isFocused={focusedPostId === post.id}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="calendar" className="mt-4">
              <CalendarView
                posts={filteredPosts}
                keywords={keywords}
                month={month}
                year={year}
                onClickPost={openEditor}
                onClickDay={openDay}
                onPrevMonth={prevMonth}
                onNextMonth={nextMonth}
              />
            </TabsContent>

            <TabsContent value="kanban" className="mt-4">
              {locationId && (
                <KanbanBoard
                  posts={filteredPosts}
                  keywords={keywords}
                  locationId={locationId}
                  onClickPost={openEditor}
                  onPublishSuccess={handlePublishSuccess}
                />
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
